import { Modal, Form, Input, Col, Row } from 'antd';
import { useEffect, useState } from 'react';
import { MyButton, MyText } from '../../../common';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { fetchDiaryAction, RootState, saveDiaryAction, selectStore } from '../../../../utils';
import { segmentButtonStyle } from './style';

interface DiaryModalProps {
  visible: boolean;
  selectedItem?: any;
  dayId: number | null; 
  childId: number;
  onOk: () => void;
  onCancel: () => void;
}

export const DiaryModal: React.FC<DiaryModalProps> = ({
  visible, selectedItem, dayId, childId, onOk, onCancel
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const saveDiary = useSelector((state: any) => state.materials?.saveDiary ?? { loading: false });
  const diaryData = useSelector((state: any) => state.materials?.diary ?? { data: null, loading: false });
  const understood = Form.useWatch('understood', form);
  const activity = Form.useWatch('activity', form);

  useEffect(() => {
    if (visible && dayId && childId) {
      dispatch(fetchDiaryAction({ dayId, childId }));
    }
  }, [visible, dayId, childId]);

  useEffect(() => {
    if (!diaryData.success) return;
    const diary = diaryData.data?.diary;

    if (diary) {
      form.setFieldsValue({
        words: diary.words_sounds,
        gestures: diary.gestures_actions,
        understood: diary.understood_instruction === true ? 'yes'
                  : diary.understood_instruction === false ? 'no'
                  : null,
        activity: diary.activity_level === 1 ? 'low'
                : diary.activity_level === 2 ? 'medium'
                : diary.activity_level === 3 ? 'high'
                : null,
        comment: diary.parent_comment,
      });
    } else {
      form.resetFields();
    }
  }, [diaryData.success, diaryData.data]);

  useEffect(() => {
    if (saveDiary.success) {
      form.resetFields();
      onOk();
    }
  }, [saveDiary.success]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: any) => {
    console.log('handleSubmit called', { values, dayId, childId });

    if (!dayId) return;

    const payload = {
      child_id: childId,
      day_id: dayId,
      words_sounds: values.words ?? '',
      gestures_actions: values.gestures ?? '',
      understood_instruction: values.understood === 'yes',
      activity_level:
        values.activity === 'low' ? 1
        : values.activity === 'medium' ? 2
        : values.activity === 'high' ? 3
        : undefined,
      parent_comment: values.comment ?? '',
    };

    dispatch(saveDiaryAction(payload));
  };
  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      width={520}
    >
      <Col span={24} style={{ marginTop: 22, marginBottom: 16 }}>
        <MyText color='#0A033C' size={32} weight={800} style={{ fontFamily: 'Nunito' }}>
          Дневник наблюдений
        </MyText>
      </Col>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>

        <Form.Item
          label={<MyText color='#4C4C4D' weight={800} style={{ fontFamily: 'Nunito' }}>Какие слова/звуки повторил ребёнок?</MyText>}
          name="words"
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea
            rows={3}
            placeholder="Любые звукоподражания, отдельные слова, фразы (даже неполные)."
            style={{ resize: 'none' }}
          />
        </Form.Item>

        <Form.Item
          label={<MyText color='#4C4C4D' weight={800} style={{ fontFamily: 'Nunito' }}>Какие жесты/действия показал?</MyText>}
          name="gestures"
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea
            rows={3}
            placeholder="Перечислите жесты, действия которые показал ребенок во время игр. Например: «пока», «дай», указательный жест, выполнение просьбы."
            style={{ resize: 'none' }}
          />
        </Form.Item>

        <Form.Item
          label={<MyText color='#4C4C4D' weight={800} style={{ fontFamily: 'Nunito' }}>Понял ли ребенок инструкции?</MyText>}
          name="understood"
          style={{ marginBottom: 4 }}
        >
          <Row style={{ outline: '1px solid #E5E9F2', borderRadius: 8, overflow: 'hidden' }}>
            <Col
              span={12}
              style={{
                  ...segmentButtonStyle(understood === 'yes'),
              }}
              onClick={() => form.setFieldValue('understood', understood === 'yes' ? null : 'yes')}
          >
              Да
            </Col>
            <Col
                span={12}
                style={{
                    ...segmentButtonStyle(understood === 'no'),
                    borderLeft: '1px solid #E5E9F2',
                }}
                onClick={() => form.setFieldValue('understood', understood === 'no' ? null : 'no')}
            >
              Нет
            </Col>
          </Row>
        </Form.Item>

        <MyText size={14} color='#364153' weight={400} style={{ fontFamily: 'Nunito', display: 'block', marginBottom: 16 }}>
          Оцените, понял ли ребёнок просьбу без показа.
        </MyText>

        <Form.Item
          label={<MyText color='#4C4C4D' weight={800} style={{ fontFamily: 'Nunito' }}>Оцените активность ребёнка</MyText>}
          name="activity"
          style={{ marginBottom: 8 }}
        >
          <Row style={{ outline: '1px solid #E5E9F2', borderRadius: 8, overflow: 'hidden' }}>
            {[
              { label: 'Низкая', value: 'low' },
              { label: 'Средняя', value: 'medium' },
              { label: 'Высокая', value: 'high' },
            ].map((item, i) => (
              <Col
                key={item.value}
                span={8}
                style={{
                  ...segmentButtonStyle(activity === item.value),
                  borderLeft: i > 0 ? '1px solid #E5E9F2' : 'none',
                }}
                onClick={() => form.setFieldValue('activity', activity === item.value ? null : item.value)}
              >
                {item.label}
              </Col>
            ))}
          </Row>
        </Form.Item>
        
        <Col span={24} style={{ marginBottom: 16 }}>
          <MyText size={14} color='#364153' weight={400} style={{ fontFamily: 'Nunito', display: 'block' }}>
            Низкая – неохотно, быстро терял интерес
          </MyText>
          <MyText size={14} color='#364153' weight={400} style={{ fontFamily: 'Nunito', display: 'block' }}>
            Средняя – участвовал частично
          </MyText>
          <MyText size={14} color='#364153' weight={400} style={{ fontFamily: 'Nunito', display: 'block' }}>
            Высокая – активно включался, заинтересован
          </MyText>
        </Col>

        <Form.Item
          label={<MyText color='#4C4C4D' weight={800} style={{ fontFamily: 'Nunito' }}>Комментарии родителя</MyText>}
          name="comment"
          style={{ marginBottom: 24 }}
        >
          <Input.TextArea
            rows={3}
            placeholder="Перечислите какие новые слова или звуки появились..."
            style={{ resize: 'none' }}
          />
        </Form.Item>

        <Form.Item style={{ textAlign: 'center', marginBottom: 8 }}>
          <MyButton
            color='orange'
            type='primary'
            htmlType='submit'
            loading={saveDiary.loading}
          >
            Отправить
          </MyButton>
        </Form.Item>

      </Form>
    </Modal>
  );
};